import type { ChartThresholdSeverity } from "@/shared/charts";
import type {
  QueueWaitSummary,
  QueueWaitTrendData,
} from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend/types";

export type QueueWaitThreshold = NonNullable<QueueWaitTrendData["thresholds"]>[number];

export function inferThresholdPresentation(
  threshold: QueueWaitThreshold,
  thresholds: readonly QueueWaitThreshold[]
): { label: string; severity: ChartThresholdSeverity } {
  const sameStateThresholds = thresholds
    .filter((candidate) => candidate.state === threshold.state)
    .sort((left, right) => left.value - right.value);
  const stateIndex = sameStateThresholds.findIndex(
    (candidate) =>
      candidate.value === threshold.value &&
      candidate.operator === threshold.operator &&
      candidate.metric === threshold.metric
  );

  if (threshold.state === "amber") {
    return stateIndex === 0
      ? { label: "Warning", severity: "warning" }
      : { label: "Medium", severity: "medium" };
  }

  return stateIndex === 0
    ? { label: "High", severity: "high" }
    : { label: "Critical", severity: "critical" };
}

export function summarizeQueueWait(data: QueueWaitTrendData): QueueWaitSummary {
  const observedValues = data.points.flatMap((point) =>
    point.averageWaitMinutes == null ? [] : [point.averageWaitMinutes]
  );

  if (observedValues.length === 0) {
    return { averageWaitMinutes: null, peakWaitMinutes: null };
  }

  return {
    averageWaitMinutes:
      observedValues.reduce((sum, value) => sum + value, 0) / observedValues.length,
    peakWaitMinutes: Math.max(...observedValues),
  };
}
